import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { inject, observer } from 'mobx-react';

import GetAppIcon from '@material-ui/icons/GetApp';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import ControlButtons from 'components/ControlButtons';
import CustomPaper from 'components/Layout/Background';

import { chart, chartTooltipData } from 'helpers/chart';
import { DetailPage } from 'pages/Accounts/Detail/enums';
import { Params } from '../accountEditRoutes';
import { RootStore } from 'stores/rootStore';
import { ApiStore } from 'stores/apiStore';
import { WorkflowBasicStore } from 'stores/workflowStores/workflowBasicStore/workflowBasicStore';
import { WorkflowPlanStore } from 'stores/workflowStores/newWorkflowPlanStore/workflowPlanStore';
import { ViewMode } from 'pages/Accounts/Edit/components/viewModes';
import Deposits from '../components/Deposits';
import Chart from './Chart/Chart';
import Risk from './Risk';
import BucketSection from './BucketSections';
import NewReserves from '../components/Reserves';
import Composition from './Composition';
import Balance from './Balance';
import AdditionalTargets from 'pages/Accounts/Edit/components/AdditionalTargets';
import ExternalProducts from 'pages/Accounts/Edit/components/ExternalProducts/ExternalProducts';
import {
  API_ACCOUNT_DOWNLOAD_CHANGED_PLAN,
  API_ACCOUNT_DOWNLOAD_DETAIL_PLAN,
  API_ACCOUNT_DOWNLOAD_PLAN,
} from 'api/endpoints';
import { AccountMode, Currency, EdwardColors, PersonType } from 'enums';
import { AccountBasic } from 'stores/workflowStores/workflowBasicStore/model';
import { FulfillmentGrade } from 'stores/workflowStores/finances/targets/model';
import { Permission } from 'helpers/permissions';
import { AccountDetailStore } from 'stores/account/accountDetailStore';

import useStyles from './styles';
import { Theme } from '@material-ui/core';

interface Props {
  rootStore?: RootStore;
  workflowBasicStore?: WorkflowBasicStore;
  apiStore?: ApiStore;
  userMode?: boolean;
  detailMode?: boolean;
  detailStore?: AccountDetailStore;
  refreshTimestamp?: string;
  planBasicData?: AccountBasic;
}

const Plan: React.FC<Props> = ({
  rootStore,
  workflowBasicStore,
  apiStore,
  detailMode,
  userMode,
  detailStore,
  refreshTimestamp,
  planBasicData,
  ...props
}) => {
  const accountIdParam = useParams<Params>().accountId;
  const accountId = rootStore?.stores.userStore.activeAccount?.accountId || accountIdParam;
  const readOnly = !!detailStore && !detailStore.editMode;
  const { t } = useTranslation();
  const [store] = useState(new WorkflowPlanStore(rootStore as RootStore, accountId, detailMode, userMode));

  const currency = detailStore?.currency || rootStore?.stores.editAccountStore.currency || Currency.NONE;
  const currentAccountTitle = detailStore?.data.detail?.title || rootStore?.stores.editAccountStore.data?.title || '';
  const currentAccountBalance = detailStore?.data.detail?.balance ||
    store.data?.depositData.initial?.value || { value: 0, currency: Currency.UNKNOWN };

  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [zeroDepositsInfo, setZeroDepositsInfo] = useState(false);
  const [taxResidencyInfo, setTaxResidencyInfo] = useState(false);
  const [esgMismatchOpen, setEsgMismatchOpen] = useState(false);
  const [targetsFullfilled, setTargetsFullfilled] = useState(false);
  const [timeTestInfo, setTimeTestInfo] = useState<boolean | undefined>(undefined);

  const [tab, setTab] = useState<'chart' | 'composition' | 'balance'>('chart');

  const extProductAllowed =
    rootStore?.stores.userStore.checkPermissions(Permission.ACCOUNT_EXT_PRODUCT) &&
    store.data?.mode === AccountMode.STANDARD &&
    store.data?.personType === PersonType.PHYSICAL;

  const personType = planBasicData?.client.personType || rootStore?.stores.editAccountStore.data?.client.personType;
  const taxResidency =
    planBasicData?.client.taxResidency || rootStore?.stores.editAccountStore.data?.client.taxResidency.country;

  const rebalancingTimeTestDisabled =
    personType === PersonType.LEGAL || (taxResidency !== 'CZE' && taxResidency !== 'SVK');

  useEffect(() => {
    const esgMismatch =
      !!store.data &&
      (!detailMode || !readOnly) &&
      !!store.data.esg &&
      ((store.data.buckets[0].portfolio.code.profile === '1' &&
        store.data.buckets[1]?.portfolio.code.profile === '1' &&
        store.data.buckets[2]?.portfolio.code.profile === '1') ||
        (store.data.portfolioOnly && store.data.buckets[2]?.portfolio.code.profile === '1')) &&
      sessionStorage.getItem(`esgMismatchConfirmed_${accountId}`) === null;
    setEsgMismatchOpen(esgMismatch);
    const allTargetsFullfilled =
      store.data?.targetData.targets.filter((t) => t.fulfillmentGrade !== FulfillmentGrade.FULL).length === 0;
    setTargetsFullfilled(allTargetsFullfilled);
  }, [store.data, accountId, detailMode, readOnly]);

  const classes = useStyles({});

  const mobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'), { noSsr: true });

  useEffect(() => {
    rootStore?.stores.editAccountStore.setStep(4);
  }, [rootStore]);

  useEffect(() => {
    (async () => {
      store.setAccountId(accountId);
      await store.fetchAccountPlan();
    })();
  }, [rootStore, store, readOnly, detailMode, accountId, refreshTimestamp]);

  const downloadPlan = useMemo(
    () => async () => {
      setPdfDownloading(true);
      await apiStore?.get(
        detailMode
          ? readOnly
            ? API_ACCOUNT_DOWNLOAD_DETAIL_PLAN(accountId)
            : API_ACCOUNT_DOWNLOAD_CHANGED_PLAN(accountId)
          : API_ACCOUNT_DOWNLOAD_PLAN(accountId),
      );
      setPdfDownloading(false);
    },
    [accountId, apiStore, detailMode, readOnly],
  );

  const refreshPlan = async () => {
    store.resetCache();
    await store.fetchAccountPlan();
  };

  const handleNext = async () => {
    if (
      // TODO: move to store
      store.data?.reserveData.reserves.length &&
      store.data?.buckets[2].rebalancingTimeTest &&
      (store.data?.buckets[0].riskProfile !== '1' || store.k1isins.includes(store.data.options.specialAssets.repo)) &&
      timeTestInfo === undefined
    ) {
      setTimeTestInfo(true);
    } else if (store.zeroDeposits) {
      setZeroDepositsInfo(true);
    } else {
      const taxResidencyOk = await store.checkTaxresidency();
      if (taxResidencyOk) {
        store.handleNext(detailStore?.goFwd);
      } else {
        setTaxResidencyInfo(true);
      }
    }
  };

  //console.log('render Plan',Date.now());

  const profileLockActive = detailMode && store.data?.profileLockActive;

  return store.data ? (
    <>
      <NewReserves
        readOnly={readOnly}
        userMode={userMode}
        viewMode={detailMode ? ViewMode.DETAIL : ViewMode.WF}
        currency={currency}
        reservesData={store.data.reserveData ? store.data.reserveData : undefined}
        accountId={accountId}
        fullWidth={true}
        collapsed={store.data.portfolioOnly}
        refresh={refreshPlan}
        plan
        planStore={store}
        openRent={store.data.rentData !== undefined ? undefined : store.setEditRent}
      />

      {/* FIXME: only pass planStore */}
      <AdditionalTargets
        accountId={accountId}
        rentData={store.data.rentData}
        targetsData={store.data.targetData.options ? store.data.targetData : undefined}
        targetsOnly={!store.data.rentApplicable}
        plan={true}
        refresh={refreshPlan}
        currency={currency}
        viewMode={detailMode ? ViewMode.DETAIL : ViewMode.WF}
        mode={store.data.mode}
        readOnly={readOnly}
        userMode={userMode}
        collapsed={store.data.portfolioOnly || store.chartComputedData.showRange}
        personType={store.data.personType}
        planStore={store}
        defaultInflation={store.data.options.inflation.defaultInflationRate}
        defaultInflationType={store.data.options.inflation.defaultInflationType}
        rentApplied={store.data.rentApplied}
        planBasicData={planBasicData}
      />

      {(extProductAllowed || store.data.externalProductData.externalProducts.length !== 0) && (
        <ExternalProducts
          accountId={accountId}
          productsData={store.data.externalProductData}
          currency={currency}
          defaultInflation={store.data.options.inflation.defaultInflationRate}
          defaultInflationType={store.data.options.inflation.defaultInflationType}
          refresh={refreshPlan}
          plan={true}
          readOnly={readOnly}
          userMode={userMode}
          collapsed={false}
          planStore={store}
          viewMode={detailMode ? ViewMode.DETAIL : ViewMode.WF}
          currentAccount={{
            title: currentAccountTitle,
            balance: currentAccountBalance,
            profit: store.data.buckets[2].portfolio.income,
          }}
        />
      )}

      <Deposits
        accountId={accountId}
        currency={currency}
        readOnly={readOnly}
        userMode={userMode}
        viewMode={detailMode ? ViewMode.DETAIL : ViewMode.WF}
        mode={store.data.mode}
        data={store.data.depositData}
        refresh={refreshPlan}
        planStore={store}
        plan
        defaultInflation={store.data.options.inflation.defaultInflationRate}
        defaultInflationType={store.data.options.inflation.defaultInflationType}
        targetsFullfilled={targetsFullfilled}
        updateTimestamp={store.fetchTimestamp}
      />

      {store.data.externalProductData.externalProducts?.length !== 0 && ( //EDS-4891
        <div className={classes.tabs}>
          <div className={tab === 'chart' ? 'activeTab' : undefined} onClick={() => setTab('chart')}>
            <Typography variant={mobile ? 'h4' : 'h3'}>{t('accountPlan_developmentInTime')}</Typography>
          </div>
          <div className={tab === 'composition' ? 'activeTab' : undefined} onClick={() => setTab('composition')}>
            <Typography variant={mobile ? 'h4' : 'h3'}>{t('externalProducts_composition')}</Typography>
          </div>
          <div className={tab === 'balance' ? 'activeTab' : undefined} onClick={() => setTab('balance')}>
            <Typography variant={mobile ? 'h4' : 'h3'}>{t('externalProducts_balance')}</Typography>
          </div>
        </div>
      )}

      {store.chartComputedData.default === false && (
        <div style={{ display: tab === 'chart' ? 'block' : 'none' }}>
          <Chart
            planData={store.data}
            data={chart(store.data, false, store.data.externalProductData?.externalProducts)}
            metaData={store.chartComputedData}
            chartTooltipData={chartTooltipData(store.data, false, store.data.externalProductData?.externalProducts)}
            currency={currency}
            stacking="normal"
            accountId={accountId}
            store={store}
            readOnly={readOnly}
            userMode={userMode}
          />
        </div>
      )}
      <CustomPaper
        padding={4}
        style={{ marginTop: '8px', position: 'relative', display: tab === 'composition' ? 'block' : 'none' }}
      >
        <Composition
          extProdData={store.data.externalProductData.externalProducts}
          composition={store.data.chart.composition}
          finInst={store.data.chart.metadata.financialInstitutions}
          toggleExtProduct={'toggleExtProd' in store ? store.toggleExtProd : undefined}
        />
      </CustomPaper>

      <CustomPaper
        padding={4}
        style={{ marginTop: '8px', position: 'relative', display: tab === 'balance' ? 'block' : 'none' }}
      >
        <Balance data={store.data.externalProductData} currency={currency} />
      </CustomPaper>

      <BucketSection
        store={store}
        accountId={accountId}
        currency={currency}
        detailMode={detailMode}
        readOnly={readOnly}
        userMode={userMode}
        rebalancingTimeTestDisabled={rebalancingTimeTestDisabled}
      />

      {!!store.data?.riskProfile && (
        <Risk
          riskProfile={store.data.riskProfile}
          changeLinkId={!detailMode ? accountId : undefined}
          changeTab={
            detailMode && !readOnly && !!detailStore ? () => detailStore.setFlowStep(DetailPage.ID) : undefined
          }
        />
      )}

      <ConfirmDialog centered noHeader isOpen={zeroDepositsInfo} confirm={() => setZeroDepositsInfo(false)}>
        {t('accountPlan_zeroDeposits_info')}
      </ConfirmDialog>

      <ConfirmDialog centered noHeader isOpen={taxResidencyInfo} confirm={() => setTaxResidencyInfo(false)}>
        {t('accountPlan_taxResidency_info')}
      </ConfirmDialog>

      <ConfirmDialog
        centered
        noHeader
        isOpen={!!timeTestInfo}
        confirm={() => {
          setTimeTestInfo(false);
          handleNext();
        }}
        close={() => setTimeTestInfo(false)}
        btnCloseText="general_back"
      >
        {t('accountPlan_timeTestK3K1RP1_info')}
      </ConfirmDialog>

      {esgMismatchOpen && (
        <ConfirmDialog
          title={null}
          btnConfirmText={t('general_continue')}
          isOpen={true}
          confirm={() => {
            sessionStorage.setItem(`esgMismatchConfirmed_${accountId}`, 'true');
            setEsgMismatchOpen(false);
          }}
          icons
        >
          <Typography style={{ fontSize: '19px', fontWeight: 'bold' }}>{t('esg_mismatchMP1')}</Typography>
          <Typography style={{ fontSize: '19px', fontWeight: 'bold', color: EdwardColors.GRAY_MEDIUM }}>
            {t('esg_mismatchContinue')}
          </Typography>
        </ConfirmDialog>
      )}

      <div className={classes.planControlButtons}>
        <ControlButtons
          onNext={handleNext}
          onBack={detailMode ? () => detailStore?.goBack() : store.handleBack}
          isSubmitting={false}
          nextText={t(detailStore?.nextStepLabel || 'accountStepper_fees')}
          backText={t(detailStore?.prevStepLabel || 'accountStepper_investmentQuestionnaire')}
          hideBack={readOnly}
          hideNext={readOnly}
          backDisabled={profileLockActive}
          backTooltipContent={profileLockActive ? t('accountDetail_questionnaire_locked') : undefined}
        >
          <Button
            variant="outlined"
            type="button"
            startIcon={pdfDownloading ? <CircularProgress size={20} /> : <GetAppIcon />}
            onClick={downloadPlan}
          >
            {`${t('general_download')} PDF`}
          </Button>
        </ControlButtons>
      </div>
    </>
  ) : null;
};

export default React.memo(inject('rootStore', 'apiStore')(observer(Plan)));
